import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  TextField,
  MenuItem,
  LinearProgress,
  Skeleton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useTheme,
  Paper,
  Rating as MuiRating,
} from "@mui/material";
import {
  Search,
  Star,
  StarBorder,
  Book,
  CalendarToday,
  Timer as TimerIcon,
  School as GraduationCap,
  ExpandMore,
  ExpandLess,
  Warning,
  Send,
  Explore,
} from "@mui/icons-material";
import axios from "axios";
import Swal from "sweetalert2";
import { motion, AnimatePresence } from "framer-motion";

// Components
import Header from "../Components/Header";
import StudentSidebar from "./StudentSdebar";

// Images
import DummyCourseImage from "../Images/DummyCourseImage.jpg";

const API_BASE_URL = "https://tdtlworld.com/dss-kaas-backend/";

const Courses = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [courses, setCourses] = useState([]);
  const [displayedCards, setDisplayedCards] = useState(3);
  const [currentReview, setCurrentReview] = useState({ review: "", rating: 0 });
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("default");
  const [isExpanded, setIsExpanded] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  const role = localStorage.getItem("role");

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("jwttoken");
    try {
      const response = await axios.get(`${API_BASE_URL}lms/user/courses/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error("Error fetching courses:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to fetch courses.",
      );
    } finally {
      setLoading(false);
    }
  };

  const submitReview = async () => {
    if (!currentReview.review.trim() || currentReview.rating === 0) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please write a review and select a rating.",
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        confirmButtonColor: "#D4AF37",
      });
      return;
    }

    const token = localStorage.getItem("jwttoken");
    try {
      await axios.post(
        `${API_BASE_URL}lms/review/${selectedCourse.course_id}/`,
        currentReview,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      Swal.fire({
        icon: "success",
        title: "Review Submitted!",
        text: "Your feedback has been recorded.",
        confirmButtonColor: "#D4AF37",
      });

      setCurrentReview({ review: "", rating: 0 });
      setShowModal(false);
    } catch (error) {
      let title = "Submission Failed";
      let text = "An unexpected error occurred.";
      if (error.response) {
        if (
          error.response.status === 400 &&
          error.response.data.error === "Cannot post multiple reviews"
        ) {
          title = "Already Reviewed";
          text = "You have already submitted a review for this course.";
        } else {
          text =
            error.response.data.detail ||
            error.response.data.error ||
            "Could not submit your review.";
        }
      }
      Swal.fire({ icon: "error", title, text, confirmButtonColor: "#D4AF37" });
    }
  };

  const filteredCourses = courses.filter(
    (course) =>
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (course.description &&
        course.description.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  const sortedCourses = [...filteredCourses].sort((a, b) => {
    switch (sortBy) {
      case "title":
        return a.title.localeCompare(b.title);
      case "progress":
        return (b.progress || 0) - (a.progress || 0);
      case "date":
        return new Date(b.start_date) - new Date(a.start_date);
      case "price":
        return (a.price || 0) - (b.price || 0);
      default:
        return 0;
    }
  });

  const CourseCardSkeleton = () => (
    <Grid item xs={12} sm={6} md={4}>
      <Card sx={{ height: "100%" }}>
        <Skeleton variant="rectangular" height={180} />
        <CardContent>
          <Skeleton variant="text" height={32} width="80%" />
          <Skeleton variant="text" height={20} width="60%" sx={{ mt: 1 }} />
          <Skeleton
            variant="rectangular"
            height={10}
            sx={{ mt: 2, borderRadius: 1 }}
          />
          <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
            <Skeleton
              variant="rectangular"
              height={36}
              sx={{ flexGrow: 1, borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              height={36}
              sx={{ flexGrow: 1, borderRadius: 1 }}
            />
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "background.default",
      }}
    >
      <Header />

      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        <StudentSidebar onToggle={setSidebarExpanded} />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 4 },
            width: sidebarExpanded ? "calc(100% - 260px)" : "calc(100% - 80px)",
            transition: theme.transitions.create(["margin", "width"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            height: "calc(100vh - 64px)",
            overflowY: "auto",
          }}
        >
          {/* Page Header */}
          <Paper
            sx={{
              p: 4,
              mb: 4,
              borderRadius: "16px",
              background: "linear-gradient(135deg, #1A1A1A 0%, #D4AF37 100%)",
              color: "white",
            }}
          >
            <Typography variant="h3" fontWeight={800}>
              My Courses
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.8 }}>
              Track your learning journey and progress.
            </Typography>
          </Paper>

          {/* Controls */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#D4AF37" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Sort By"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="title">Title (A-Z)</MenuItem>
                <MenuItem value="progress">Progress (High-Low)</MenuItem>
                <MenuItem value="date">Start Date (Newest)</MenuItem>
                <MenuItem value="price">Price (Low-High)</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {/* Content */}
          <AnimatePresence mode="wait">
            {loading ? (
              <Grid container spacing={3} key="loading">
                {Array.from(new Array(3)).map((_, i) => (
                  <CourseCardSkeleton key={i} />
                ))}
              </Grid>
            ) : error ? (
              <Box sx={{ textAlign: "center", py: 8 }} key="error">
                <Alert
                  icon={<Warning />}
                  severity="error"
                  sx={{ maxWidth: 500, mx: "auto", borderRadius: "12px" }}
                >
                  {error}
                </Alert>
                <Button
                  variant="contained"
                  onClick={fetchCourses}
                  sx={{ mt: 3 }}
                >
                  Try Again
                </Button>
              </Box>
            ) : sortedCourses.length > 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                key="courses"
              >
                <Grid container spacing={3}>
                  {sortedCourses.slice(0, displayedCards).map((course) => (
                    <Grid item xs={12} sm={6} md={4} key={course.course_id}>
                      <Card
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          border: "1px solid",
                          borderColor: "divider",
                          transition: "transform 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-5px)",
                            borderColor: "#D4AF37",
                          },
                        }}
                      >
                        <Box sx={{ position: "relative" }}>
                          <CardMedia
                            component="img"
                            height="180"
                            image={course.cover_img || DummyCourseImage}
                            alt={course.title}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              top: 10,
                              right: 10,
                              bgcolor: "#D4AF37",
                              color: "black",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "50px",
                              fontWeight: 700,
                              fontSize: "0.8rem",
                            }}
                          >
                            {course.price ? `₹${course.price}` : "Free"}
                          </Box>
                        </Box>

                        <CardContent
                          sx={{
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            gutterBottom
                            noWrap
                            color="#D4AF37"
                          >
                            {course.title}
                          </Typography>

                          <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <CalendarToday
                                sx={{
                                  fontSize: "0.9rem",
                                  color: "text.secondary",
                                }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {new Date(
                                  course.start_date,
                                ).toLocaleDateString()}
                              </Typography>
                            </Box>
                            {course.time_commitment && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <TimerIcon
                                  sx={{
                                    fontSize: "0.9rem",
                                    color: "text.secondary",
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {course.time_commitment}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 2,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              height: "40px",
                            }}
                          >
                            {course.description}
                          </Typography>

                          {role === "Instructor" ? (
                            <Box
                              sx={{
                                mt: "auto",
                                bgcolor: "rgba(212, 175, 55, 0.05)",
                                p: 1,
                                borderRadius: 1,
                              }}
                            >
                              <Typography variant="caption" display="block">
                                Students:{" "}
                                <strong>{course.students || 0}</strong>
                              </Typography>
                              <Typography variant="caption" display="block">
                                Revenue:{" "}
                                <strong>
                                  ₹{course.revenue?.toFixed(2) || "0.00"}
                                </strong>
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ mt: "auto" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <Typography variant="caption" fontWeight={600}>
                                  Progress
                                </Typography>
                                <Typography variant="caption" fontWeight={700}>
                                  {Math.ceil((course.progress || 0) * 100)}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={Math.ceil((course.progress || 0) * 100)}
                                sx={{
                                  height: 8,
                                  borderRadius: 5,
                                  bgcolor: "rgba(212, 175, 55, 0.1)",
                                  "& .MuiLinearProgress-bar": {
                                    bgcolor: "#D4AF37",
                                  },
                                }}
                              />
                            </Box>
                          )}

                          <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<Book />}
                              onClick={() =>
                                navigate(
                                  `/db-shiksha/kaas/student/course/${course.course_id}`,
                                )
                              }
                            >
                              Explore
                            </Button>
                            {role !== "Instructor" && (
                              <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<Star />}
                                onClick={() => {
                                  setSelectedCourse(course);
                                  setShowModal(true);
                                }}
                                sx={{
                                  borderColor: "#D4AF37",
                                  color: "#D4AF37",
                                }}
                              >
                                Review
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {sortedCourses.length > 3 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 4 }}
                  >
                    <Button
                      variant="text"
                      onClick={() => {
                        setIsExpanded(!isExpanded);
                        setDisplayedCards(
                          isExpanded ? 3 : sortedCourses.length,
                        );
                      }}
                      endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                      sx={{ color: "#D4AF37", fontWeight: 700 }}
                    >
                      {isExpanded ? "Show Less" : "Show All Courses"}
                    </Button>
                  </Box>
                )}
              </motion.div>
            ) : (
              <Box sx={{ textAlign: "center", py: 10 }} key="empty">
                <GraduationCap
                  sx={{ fontSize: 80, color: "text.disabled", mb: 2 }}
                />
                <Typography variant="h5" color="text.secondary">
                  No Courses Found
                </Typography>
                <Button
                  variant="contained"
                  sx={{ mt: 3 }}
                  onClick={() => navigate("/db-shiksha/kaas/student/coursePage")}
                  startIcon={<Explore />}
                >
                  Explore Courses
                </Button>
              </Box>
            )}
          </AnimatePresence>
        </Box>
      </Box>

      {/* Review Dialog */}
      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Review: {selectedCourse?.title}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Your Feedback:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Tell us what you think..."
            value={currentReview.review}
            onChange={(e) =>
              setCurrentReview({ ...currentReview, review: e.target.value })
            }
            sx={{ mb: 3 }}
          />
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="subtitle2" gutterBottom>
              Rate this Course:
            </Typography>
            <MuiRating
              value={currentReview.rating}
              onChange={(event, newValue) =>
                setCurrentReview({ ...currentReview, rating: newValue })
              }
              size="large"
              icon={<Star sx={{ color: "#D4AF37" }} />}
              emptyIcon={<StarBorder />}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowModal(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={submitReview}
            variant="contained"
            disabled={
              !currentReview.review.trim() || currentReview.rating === 0
            }
            startIcon={<Send />}
          >
            Submit Review
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Courses;
